import { useParams, Link, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import Meta from './Meta';

interface CounsellorData {
  name: string;
  title: string;
  image: string;
  languages: string[];
  description: string;
}

const counsellorData: { [key: string]: CounsellorData } = {
  'kristina-sohal': {
    name: "Kristina Sohal",
    title: "RCC, Registered Clinical Counsellor",
    image: "/images/Kristina_Sohal-optimized.webp",
    languages: ["English", "Punjabi"],
    description: `I'm Kristina, a registered counsellor in Vancouver who helps transform lives through compassionate and non-judgmental therapy.

**My Journey**
My path to counselling began in the finance industry, where I worked for several years. While helping clients with financial stress, I discovered my true passion for supporting people through their challenges. This led me to pursue a career in therapy, earning my Masters of Counselling Psychology from Yorkville University.

**My Approach**
As a member of the British Columbia Association of Counsellors, I take pride in:
• Providing a safe, non-judgmental space for healing
• Taking time to truly listen and understand each client's unique journey
• Offering personalized tools for positive life changes

**Therapeutic Methods**
I employ various evidence-based approaches including:
• Cognitive Behavioural Therapy (CBT)
• Mindful Exercises
• Solution-Focused Therapy
• Person Centered Therapy
• Exposure Therapy
• Empty Chair Techniques`,
  },
  'sarvenaz-amiri': {
    name: "Sarvenaz Amiri",
    title: "RCC, Registered Clinical Counsellor",
    image: "/images/sarvenaz-amiri-optimized.webp",
    languages: ["English", "Farsi"],
    description: `**Professional Background**
As a Registered Clinical Counsellor (RCC), I am dedicated to supporting individuals through life's challenges with compassion, understanding, and evidence-based care. My clinical experience includes working with clients experiencing:
• Anxiety and depression
• Trauma and PTSD
• ADHD and emotional regulation difficulties
• Relationship and family concerns
• Parenting challenges
• Life transitions and adjustment issues
• Self-esteem and identity development

**Therapeutic Approaches**
My work is grounded in a client-centered, trauma-informed, and culturally responsive framework. I integrate evidence-based approaches including:
• Cognitive Behavioral Therapy (CBT)
• Solution-Focused Therapy
• Strengths-Based Therapy
• Mindfulness-Based Interventions
• Attachment-Informed Approaches
• Trauma-Informed Care

**Areas of Focus**
I support clients in:
• Healing from difficult life experiences and trauma
• Managing anxiety, depression, and stress
• Building emotional resilience and self-awareness
• Improving relationships and communication skills
• Navigating family and parenting challenges
• Strengthening self-confidence and personal growth
• Creating meaningful and lasting life changes

My passion is helping individuals better understand themselves, overcome barriers to growth, and build healthier relationships with themselves and others. Together, we work toward greater balance, resilience, and a life that feels authentic and fulfilling.`,
  },
  'cynthia-csepregi': {
    name: "Cynthia Csepregi",
    title: "RCC, BA in Social Work",
    image: "/images/Cynthia-Csepregi-min-optimized.webp",
    languages: ["English"],
    description: `**Welcome**
I'm Cynthia, and I'm excited to be part of the Metro Counselling team. As a registered Social Worker and Counsellor, I believe strongly in people's capacity for positive change.

**Professional Journey**
My career path includes:
• 14 years in complex mental health settings
• Extensive work in non-profit organizations
• Experience with developmental disabilities programs
• Masters in Counselling from City University of Seattle
• Specialized research in the impact of suicide on families

**Counselling Philosophy**
I bring to my practice:
• A strengths-based approach
• Extensive mental health experience
• Deep empathy and understanding
• Commitment to supporting positive change
• Specialized crisis intervention training

My greatest satisfaction comes from helping clients become architects of change in their own lives. I look forward to being part of your journey toward positive transformation.`,
  },
  'fatemeh-poorrezvan': {
    name: "Fatemeh Poorrezvan",
    title: "RCC, Registered Clinical Counsellor",
    image: "/images/venus1-optimized.webp",
    languages: ["English", "Farsi", "Turkish"],
    description: `**Professional Credentials**
• BCACC Registered Clinical Counselor
• Member of the American Psychological Association (APA)
• Master's degree in Clinical Psychology
• Specialized training in Autism and Neurodiversity
• Expertise in Addiction Management

**Research & Experience**
My research has focused on:
• Hope and expectation in medical settings
• Behavioral discipline and self-efficacy
• Anxiety management in surgical patients
• 13+ years of clinical consultation experience
• Extensive work with diverse patient populations

**Clinical Approach**
I am inspired by:
• Human resilience and healing potential
• Client-centered therapy
• Evidence-based practices
• Collaborative treatment planning
• Multicultural counselling perspectives

Through my hospital experience and work with thousands of patients, I've developed a deep appreciation for the human capacity for growth and healing.`,
  },
  'rafael-tejada': {
    name: "Rafael Tejada",
    title: "RCC, Registered Clinical Counsellor",
    image: "/images/rafeal-optimized.webp",
    languages: ["English", "Spanish"],
    description: `**Areas of Expertise**
I specialize in helping clients navigate:
• Trauma and recovery
• Anxiety and depression
• Personality disorders
• Addiction challenges
• Relationship dynamics
• Family communication

**Therapeutic Focus**
My work centers on:
• Examining attachment difficulties
• Healing relationship injuries
• Building self-understanding
• Developing healthy connections
• Restoring self-esteem and hope

**Counselling Philosophy**
I believe in:
• Facing challenges with kindness
• Building compassionate perspectives
• Embracing our whole selves
• Working collaboratively toward goals
• Creating lasting positive change

Together, we'll work to understand your experiences and build a path toward inner peace and stronger relationships.`,
  },
  'beth-mares': {
    name: "Beth Mares",
    title: "Self-Care Coach & Therapeutic Touch Practitioner",
    image: "/images/beth_hq-optimized.webp",
    languages: ["English"],
    description: `Beth is a seasoned Self-Care Coach and Therapeutic Touch Practitioner with extensive experience in Quantum and Ancient Healing Modalities. Specializing in Holistic Bodywork, Massage, and Therapeutic Touch Techniques, Beth's practice blends scientific rigor with spiritual wisdom.

With her compassionate touch, Beth harmonizes individuals' energy fields, aiming to balance and clear their bio-energetic systems. She has spent years working in medical settings, helping diverse clients heal from chronic health conditions. Beth is convinced that energy work and self-care are pivotal for the future of medicine.`,
  },
  'albin-issac': {
    name: "Albin Issac",
    title: "RCC, Registered Clinical Counsellor",
    image: "/images/albin-optimized.webp",
    languages: ["English", "Malayalam", "Tamil"],
    description: `I'm Albin, a Registered Clinical Counsellor in British Columbia dedicated to helping individuals, couples, and families build resilience, strengthen relationships, and find hope through life's challenges.

**My Journey**
My path to counselling began with a strong interest in supporting people through emotional and relational struggles. I earned my Master of Science in Counselling and Family Therapy and am completing my Master of Arts in Leadership (Healthcare) at Trinity Western University. This academic foundation, combined with practical clinical experience, allows me to provide holistic and client-centered care.

**My Approach**
As a member of the British Columbia Association of Clinical Counsellors, I take pride in:
• Creating a safe, supportive, and culturally sensitive space
• Listening deeply to understand each client's unique journey
• Offering strategies that promote emotional well-being, healing, and growth

**Therapeutic Methods**
I use a range of evidence-based approaches tailored to client needs, including:
• Cognitive Behavioral Therapy (CBT)
• Solution-Focused Brief Therapy (SFBT)
• Trauma-Focused CBT (TF-CBT)
• Emotionally Focused Therapy (EFT) for couples and families
• Mindfulness-Based Cognitive Therapy (MBCT)
• Person-Centered Therapy
• Christian Counselling Approaches
• Trauma-Informed Practice

**BCACC Membership ID: 24646**`,
  },
  'karen-gorseth': {
    name: "Karin Gorseth",
    title: "MSW-RSW, Registered Social Worker",
    image: "/images/karen_gorseth.webp",
    languages: ["English"],
    description: `Karin Gorseth works with individuals and families in therapy to bring deeper meaning and clarity to their lives. She believes in honoring the experiences of others as a vehicle for self-exploration and deepened understanding of oneself.

**Professional Background**
Certified in psychoanalytic psychotherapy from the Contemporary Freudian Society (New York City). Master of Social Work from Hunter College Silberman School of Social Work. Bachelor's degree in Social Work from Seattle University.

**Areas of Expertise**
- Individual and family therapy
- Trauma-focused treatment modalities
- Psychoanalytic psychotherapy
- Life transitions and personal growth
- Specialized training in trauma-focused treatment approaches

**Approach to Therapy**
Karin's therapeutic approach is rooted in creating a safe, non-judgmental space where clients can explore their thoughts and feelings. She works collaboratively with clients to develop personalized strategies that align with their goals and values.`,
  },
};

const CounsellorBio = () => {
  const { counsellorId } = useParams<{ counsellorId: string }>();
  const counsellor = counsellorId ? counsellorData[counsellorId] : null;
  const navigate = useNavigate();

  const handleBackClick = () => {
    // Navigate to home page
    navigate('/');
    // Wait for navigation to complete then scroll to counsellors section
    setTimeout(() => {
      const counsellorsSection = document.querySelector('.bg-white.py-12');
      if (counsellorsSection) {
        counsellorsSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  if (!counsellor) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900">Counsellor not found</h2>
          <Link to="/" className="mt-4 text-blue-600 hover:text-blue-800">
            Return to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Meta 
        title={`${counsellor.name} - ${counsellor.title} | Metro Counselling Surrey`}
        description={`Meet ${counsellor.name}, ${counsellor.title} at Metro Counselling. ${counsellor.description.split('\n')[0]}`}
      />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-b from-blue-50 to-white pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="flex flex-col lg:flex-row items-start gap-12">
            {/* Image Column */}
            <div className="w-full lg:w-80 flex-shrink-0">
              <div className="rounded-lg overflow-hidden shadow-md bg-white">
                <img
                  src={counsellor.image}
                  alt={counsellor.name}
                  className="w-full h-96 object-cover object-top"
                />
              </div>
              
              {/* Languages Section - Moved under image on mobile */}
              <div className="mt-6 bg-white p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-medium text-gray-900 mb-3">Languages</h3>
                <div className="flex flex-wrap gap-2">
                  {counsellor.languages.map((language) => (
                    <span
                      key={language}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
                    >
                      {language}
                    </span>
                  ))}
                </div>
              </div>

              {/* Contact Button */}
              <Link
                to="/contact-us"
                className="mt-6 w-full inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors duration-200"
              >
                Book an Appointment
              </Link>
            </div>

            {/* Content Column */}
            <div className="flex-grow">
              <h1 className="text-4xl font-bold text-gray-900 mb-2">{counsellor.name}</h1>
              <p className="text-xl text-gray-600 mb-8">{counsellor.title}</p>
              
              <div className="prose prose-lg max-w-none">
                <ReactMarkdown
                  components={{
                    p: (props) => <p className="mb-6 text-gray-700" {...props} />,
                    strong: (props) => <strong className="text-gray-900 font-semibold" {...props} />,
                    ul: (props) => <ul className="list-disc pl-6 mb-6" {...props} />,
                    li: (props) => <li className="text-gray-700 mb-2" {...props} />,
                    h2: (props) => <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4" {...props} />
                  }}
                >
                  {counsellor.description}
                </ReactMarkdown>
              </div>

              {/* Navigation */}
              <div className="mt-12 flex items-center justify-between border-t border-gray-200 pt-8">
                <button
                  onClick={handleBackClick}
                  className="mb-6 px-4 py-2 text-gray-600 hover:text-gray-800 flex items-center gap-2 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L4.414 9H17a1 1 0 110 2H4.414l5.293 5.293a1 1 0 010 1.414z" clipRule="evenodd" />
                  </svg>
                  Back to Counsellors
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CounsellorBio;
